"use client";

import { useEffect, useRef } from 'react';
import { usePostHog } from '@/hooks/usePostHog';
import { useCart } from '@/context/CartContext';

export default function CartAbandonmentTracker() {
  const { track } = usePostHog();
  const { cart } = useCart();
  const lastCartUpdate = useRef<number>(Date.now());
  const abandonmentTimerRef = useRef<NodeJS.Timeout>();
  const sessionStartTime = useRef<number>(Date.now());

  // Track cart abandonment after 10 minutes of inactivity
  const ABANDONMENT_TIMEOUT = 10 * 60 * 1000; // 10 minutes

  useEffect(() => {
    // Clear existing timer
    if (abandonmentTimerRef.current) {
      clearTimeout(abandonmentTimerRef.current);
    }

    // Only set abandonment timer if cart has items
    if (cart && cart.lines.edges.length > 0) {
      lastCartUpdate.current = Date.now();

      abandonmentTimerRef.current = setTimeout(() => {
        track('cart_abandoned', {
          cart_total: parseFloat(cart.cost.totalAmount.amount),
          cart_quantity: cart.totalQuantity,
          items_count: cart.lines.edges.length,
          currency: cart.cost.totalAmount.currencyCode,
          time_in_cart_minutes: Math.round((Date.now() - lastCartUpdate.current) / (1000 * 60)),
          session_duration_minutes: Math.round((Date.now() - sessionStartTime.current) / (1000 * 60))
        });
      }, ABANDONMENT_TIMEOUT);
    }

    return () => {
      if (abandonmentTimerRef.current) {
        clearTimeout(abandonmentTimerRef.current);
      }
    };
  }, [cart, track]);

  // Track page visibility changes for better abandonment detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && cart && cart.lines.edges.length > 0) {
        track('cart_session_paused', {
          cart_total: parseFloat(cart.cost.totalAmount.amount),
          cart_quantity: cart.totalQuantity,
          time_in_session_minutes: Math.round((Date.now() - sessionStartTime.current) / (1000 * 60))
        });
      } else if (document.visibilityState === 'visible' && cart && cart.lines.edges.length > 0) {
        track('cart_session_resumed', {
          cart_total: parseFloat(cart.cost.totalAmount.amount),
          cart_quantity: cart.totalQuantity
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [cart, track]);

  return null;
}